import Vue from "vue";
import Vuex from "vuex";

import { GetSessionInfo } from "@/services/user/GetSessionInfo";
import { SessionFranchiseType, SessionInfoType } from "@/types/user/SessionInfo";

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    snackbar: { active: false, color: "", message: "", timer: 4000 },
    session: null as SessionInfoType | null,
    sessionLoading: false,
    sessionLoadPromise: null as Promise<SessionInfoType> | null,
  },
  getters: {
    isSessionReady(state): boolean {
      return !!state.session && !state.sessionLoading;
    },
    sessionFranchises(state): SessionFranchiseType[] {
      return state.session?.franchises ?? [];
    },
    isAssociatesRegistrar(state): boolean {
      return !!state.session?.canRegisterAssociates;
    },
    isAssociateProfile(state): boolean {
      return !!state.session?.associateId;
    },
    hasNoFranchiseFilterProfile(state, getters): boolean {
      return getters.isAssociateProfile && !getters.isCollaboratorManager;
    },
    isCollaboratorManager(state, getters): boolean {
      const isAdminInAnyFranchise = getters.sessionFranchises.some(
        (franchise: SessionFranchiseType) => franchise.isAdmin
      );
      return isAdminInAnyFranchise;
    },
    canUsePremiumVouchers(state, getters): boolean {
      return getters.isAssociateProfile || getters.isCollaboratorManager;
    },
    canAccessAssociates(state, getters): boolean {
      return (
        getters.isCollaboratorManager ||
        getters.isAssociatesRegistrar ||
        getters.isAssociateProfile
      );
    },
    isFranchiseAdmin(state, getters): boolean {
      return getters.sessionFranchises.some(
        (franchise: SessionFranchiseType) => franchise.isAdmin,
      );
    },
    isFranchiseAssociateInactive(state): boolean {
      return (
        !!state.session?.associateId &&
        state.session?.associateStatus === "INACTIVE"
      );
    },
    isFranchiseAssociatePending(state): boolean {
      return (
        !!state.session?.associateId &&
        state.session?.associateStatus === "PENDING"
      );
    },
    isFirstLogin(state): boolean {
      return !!state.session?.isFirstLogin;
    },
    isAssociatePremiumActive(state, getters): boolean {
      if (getters.isCollaboratorManager) return true;
      return !!state.session?.isAssociatePremiumActive;
    },
    isAssociatePremiumRenewalDue(state): boolean {
      return !!state.session?.associatePremiumRenewalDueSoon;
    },
    isAssociatePremiumExpired(state): boolean {
      return !!state.session?.associatePremiumExpired;
    },
    shouldShowPremiumUpsell(state, getters): boolean {
      if (getters.isCollaboratorManager) return false;
      if (!getters.canUsePremiumVouchers) return false;
      if (getters.isAssociatePremiumActive) return false;
      if (getters.isFranchiseAssociateInactive) return false;
      if (getters.isFranchiseAssociatePending) return false;
      return !!state.session?.canRegisterAssociates;
    },
  },
  mutations: {
    UPDATE_SNACKBAR(state, snackbar) {
      state.snackbar = snackbar;
    },
    SET_SESSION(state, session: SessionInfoType | null) {
      state.session = session;
    },
    SET_SESSION_LOADING(state, loading: boolean) {
      state.sessionLoading = loading;
    },
    SET_SESSION_LOAD_PROMISE(state, promise: Promise<SessionInfoType> | null) {
      state.sessionLoadPromise = promise;
    },
  },
  actions: {
    showSnack({ commit }, { message, color, timer }) {
      commit("UPDATE_SNACKBAR", {
        active: true,
        color: color,
        message: message,
        timer: timer,
      });
    },
    async loadSession({ commit, state }) {
      if (state.session) {
        return state.session;
      }

      if (state.sessionLoadPromise) {
        return state.sessionLoadPromise;
      }

      const getSessionInfo = new GetSessionInfo();
      commit("SET_SESSION_LOADING", true);

      const promise = getSessionInfo
        .execute()
        .then((sessionInfo) => {
          commit("SET_SESSION", sessionInfo);
          return sessionInfo;
        })
        .finally(() => {
          commit("SET_SESSION_LOADING", false);
          commit("SET_SESSION_LOAD_PROMISE", null);
        });

      commit("SET_SESSION_LOAD_PROMISE", promise);
      return promise;
    },
    async refreshSession({ commit }) {
      const getSessionInfo = new GetSessionInfo();
      commit("SET_SESSION_LOADING", true);
      try {
        const sessionInfo = await getSessionInfo.execute();
        commit("SET_SESSION", sessionInfo);
        return sessionInfo;
      } finally {
        commit("SET_SESSION_LOADING", false);
        commit("SET_SESSION_LOAD_PROMISE", null);
      }
    },
    clearSession({ commit }) {
      commit("SET_SESSION", null);
      commit("SET_SESSION_LOADING", false);
      commit("SET_SESSION_LOAD_PROMISE", null);
    },
  },
  modules: {},
});
